import React from "react";
import PolicyLayout from "../PolicyLayout";
import { highlightKeyInfo, linkifyNbfc } from "../NbfcLink";
import { CUSTOMER_SUPPORT_HOURS, VOICE_SUPPORT_HOURS } from "../../utils/supportHours";

const PrivacyPolicy: React.FC = () => {
  const policySections = [
    {
      title: `${import.meta.env.VITE_COMPANY_NBFC} (Brand Name: ${import.meta.env.VITE_COMPANY_NAME})`,
      content:
        'This Privacy Policy governs the use of the website accessible at https://loaninseconds.com/ ("Website" or "Site") legally registered under LOANINSECONDS. We are committed to upholding the privacy and confidentiality of our users ("User" or "you") and ensure that your personal data is handled in accordance with applicable legal standards.',
    },
    {
      title: "1. Acceptance of Terms",
      content:
        "By accessing or using this Website and any of its sub-links, you acknowledge and agree to the terms of this Privacy Policy. Continued use of the Website constitutes your express consent to the collection, use, disclosure, and processing of personal information by us in accordance with this Policy.",
    },
    {
      title: "1A. Consent to Use of Platform",
      content:
        `By visiting our Booking Platform or by providing your information, you consent to the collection, use, storage, disclosure and otherwise processing of your information (including sensitive personal information) on the Booking Platform in accordance with this Privacy Policy.

If you disclose to us any personal information relating to other people or make bookings on their behalf, you represent that you have the authority to do so and to permit us to use and share the information in accordance with this Privacy Policy.

You, while providing your personal information over the Platform or any partner platforms or establishments, consent to us (including our other corporate entities, affiliates, partners, technology partners, marketing channels, business partners, and other third parties) to contact you through SMS, RCS, instant messaging apps, calls and/or email for the purposes specified in this Privacy Policy.`,
    },
    {
      title: "2. Collection of Personal Information",
      content:
        "We do not collect personal data unless voluntarily submitted by you. Any such data voluntarily provided shall not be sold, rented, or otherwise transferred to unrelated third parties, except where expressly stated or with your prior consent.",
    },
    {
      title: "3. Use and Disclosure of Information",
      content:
        "Personal information collected shall be treated as private and confidential. We shall not disclose such information to third parties except under the following circumstances:",
      list: [
        "Compliance with applicable laws, regulations, legal proceedings, or government requests;",
        "Protection and defense of our rights or property or those of other users;",
        "Enforcement of the Website's terms of use;",
        "In connection with any merger, acquisition, restructuring, or sale of business or assets, where the successor entity will be bound by this Privacy Policy.",
      ],
    },
    {
      title: "4. Communications and Marketing",
      content:
        "Unless expressly opted out by you, we may use your email address to disseminate promotional or service-related communications. You retain the right to unsubscribe from such communications through provided mechanisms.",
    },
    {
      title: "5. Data Analytics and Aggregation",
      content:
        "We may collect PAN, Aadhaar, bank details, credit history, and transactional information during the loan process. These are protected under applicable data privacy laws. Such information may be used to assess Website performance and user engagement. Only aggregated data may be shared with advertisers or third parties, ensuring no individual identification.",
    },
    {
      title: "6. Security of Communications",
      content:
        "We endeavor to safeguard all communications; however, we do not warrant that emails or data transmissions via the internet are completely secure. You are advised to exercise discretion when sharing sensitive information online.",
    },
    {
      title: "7. Intellectual Property and Use Restrictions",
      content:
        "All content on this Website, including but not limited to text, graphics, logos, and trademarks, is our intellectual property. Any reproduction, modification, distribution, republication, or commercial exploitation of content without our express written permission is strictly prohibited and constitutes an infringement of intellectual property rights.",
    },
    {
      title: "8. Use by Minors",
      content:
        'Use of the Website is restricted to individuals who are legally competent to enter into binding contracts under Indian law. Persons deemed "incompetent to contract" under the Indian Contract Act, 1872—including minors and un-discharged insolvents—are prohibited from using this Website.',
    },
    {
      title: "9. Disclaimer and Limitation of Liability",
      content:
        'The Website and its content are provided on an "as is" and "as available" basis. We make no representations or warranties, express or implied, regarding the accuracy, adequacy, or completeness of the content. We disclaim all liabilities for damages arising from the use of or inability to use the Website, including but not limited to direct, indirect, incidental, or consequential losses.',
    },
    {
      title: "10. Third-Party APIs and Data Protection",
      content:
        "Our services may integrate with third-party Application Programming Interfaces (APIs), including those provided by RBI-regulated entities, to enhance functionality and access real-time information. While we rely on the availability and accuracy of data from these external sources, we do not guarantee their uninterrupted operation or completeness. We are committed to protecting your personal and financial information. All data accessed or shared via third-party APIs is handled securely and in compliance with applicable data protection laws. Your data remains protected and is shared only with trusted, regulated entities under appropriate safeguards.",
    },
    {
      title: "11. Data Retention & Preservation",
      content:
        "We retain personal and financial data for the duration necessary to fulfill regulatory, legal, and operational requirements. In particular, data is stored in accordance with the retention norms prescribed by the Reserve Bank of India (RBI), which may include specific minimum and maximum retention periods. After the applicable retention period, data is securely deleted, anonymized, or archived, unless required otherwise by law.",
    },
    {
      title: "12. Submission of Information",
      content:
        "All information submitted to us through the Website shall be deemed our property. You should exercise caution when inputting personal data to ensure it is not visible to unauthorized third parties.",
    },
    {
      title: "13. Changes to Privacy Policy",
      content:
        "We reserve the right to amend or modify this Privacy Policy at any time without prior notice. You are advised to review the policy periodically to remain informed of any changes. Continued use of the Website constitutes your acceptance of the modified policy.",
    },
    {
      title: "14. Cookies",
      content:
        'We may employ "cookies" to enhance your experience. Cookies are session-specific and do not access any data from your device. Third-party cookies may be encountered, and we disclaim any liability for such third-party practices.',
    },
    {
      title: "15. Disclosure",
      content: "The Grievance Redressal Officer as required under the Consumer Protection Act, 2019:",
      list: ["Name: Ms. Akshita Dalal | Contact Number: 8796755543 | Email ID: grievance@loaninseconds.com"],
    },
    {
      title: "Contact Us",
      content: `For General Queries: info@loaninseconds.com
For Existing Customers: support@loaninseconds.com
Phone: 8796755546

Voice Support Hours: ${VOICE_SUPPORT_HOURS}
Customer Support Hours: ${CUSTOMER_SUPPORT_HOURS}`,
    },
  ];

  return (
    <PolicyLayout 
      title="Privacy Policy" 
      subtitle={`At ${import.meta.env.VITE_COMPANY_NAME}, we guard your data with bank-grade security. Your privacy is our professional commitment and institutional priority.`}
      badge="Data Protection 🔒"
    >
      {policySections.map((section, idx) => (
        <div key={idx} className="space-y-6">
          <h2 className="text-2xl font-semibold text-[#CC1A1A] tracking-tighter leading-tight">
            {linkifyNbfc(section.title)}
          </h2>

          {section.content && (
            <div className="text-gray-500 text-base leading-relaxed font-light whitespace-pre-line">
              {highlightKeyInfo(section.content)}
            </div>
          )}
          
          {section.list && (
            <ul className="space-y-4">
              {section.list.map((li, i) => (
                <li key={i} className="flex items-start gap-3 text-gray-500 text-base leading-relaxed font-light">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#CC1A1A] mt-2.5 flex-shrink-0" />
                  {highlightKeyInfo(li)}
                </li>
              ))}
            </ul>
          )}
        </div>
      ))}
    </PolicyLayout>
  );
};

export default PrivacyPolicy;